import { TransactionDetailSection } from '@/components/DataReflection/TransactionDetailSection';
import {
  TransactionTooltips,
  WarningMessageProps,
} from '@/components/DataReflection/TransactionDetailSection/types';
import { OcrInferenceResult } from '@/features/InvoiceReport/Edit/components/type';
import dayjs from 'dayjs';
import { useMemo } from 'react';

export type PaymentDetailListProps = {
  transactionList?: OcrInferenceResult['invoiceTransactions'];
  inferences: OcrInferenceResult['inferences'];
  isSelected: boolean;
  warningMessage?: WarningMessageProps;
  canDisplayDrExcise?: boolean;
};

/**
 * Mapping between OCR inference keys and tooltip display field keys
 */
type InferenceKey = keyof NonNullable<
  OcrInferenceResult['inferences'][number]['invoiceTransactions'][number]
>;

type InferenceDisplayFieldKey =
  | 'dealDate'
  | 'name'
  | 'deptName'
  | 'exItemName'
  | 'projectName'
  | 'crItemName'
  | 'crSubItemName';

const INFERENCE_TO_DISPLAY_KEY: Record<InferenceKey, InferenceDisplayFieldKey> =
  {
    dealDate: 'dealDate',
    name: 'name',
    dept: 'deptName',
    exItem: 'exItemName',
    project: 'projectName',
    crItem: 'crItemName',
    crSubItem: 'crSubItemName',
  } as const;

/** Type guard: Check if a string is a valid inference key */
const isInferenceKey = (key: string): key is InferenceKey => {
  return key in INFERENCE_TO_DISPLAY_KEY;
};

export const PaymentDetailList = ({
  transactionList: _transactionList = [],
  inferences,
  isSelected,
  warningMessage,
  canDisplayDrExcise = false,
}: PaymentDetailListProps) => {
  const transactionList = _transactionList.map((item) => ({
    dealDate: item.dealDate
      ? dayjs(item.dealDate).format('YYYY/MM/DD')
      : undefined,
    name: item.name,
    deptName: item.dept?.name,
    exItemName: item.exItem?.name,
    totalValueExcludingTax: item.totalValue,
    totalValueIncludingTax: item.totalTaxIncludeValue,
    ...(canDisplayDrExcise
      ? { exciseName: item.drExcise?.name, exiseValue: item.exciseValue }
      : {}),
    exciseName: item.drExcise?.name,
    exiseValue: item.exciseValue,
    projectName: item.project?.name,
    crItemName: item.crItem?.name,
    crSubItemName: item.crSubItem?.name,
  }));

  const fields = [
    { key: 'dealDate', label: '取引日' },
    { key: 'name', label: '品目' },
    { key: 'deptName', label: '費用負担部門' },
    { key: 'exItemName', label: '経費科目' },
    { key: 'totalValueExcludingTax', label: '金額（税抜）' },
    ...(canDisplayDrExcise
      ? [
          { key: 'exciseName', label: '税区分' },
          { key: 'exiseValue', label: '消費税額' },
        ]
      : []),
    { key: 'projectName', label: 'プロジェクト' },
    { key: 'crItemName', label: '貸方科目' },
    { key: 'crSubItemName', label: '貸方科目補助科目' },
  ];

  const tooltips = useMemo(() => {
    if (!inferences || inferences.length === 0) {
      return {};
    }
    const invoiceTransactions = inferences[0]?.invoiceTransactions ?? [];

    if (invoiceTransactions.length === 0) {
      return {};
    }

    const tooltips: TransactionTooltips = {};
    invoiceTransactions.forEach((transactionInference, transactionIndex) => {
      if (!transactionInference) return;

      Object.entries(transactionInference).forEach(
        ([inferenceKey, inferenceDetail]) => {
          if (!inferenceDetail || typeof inferenceDetail !== 'object') {
            return;
          }

          const displayKey = isInferenceKey(inferenceKey)
            ? INFERENCE_TO_DISPLAY_KEY[inferenceKey]
            : inferenceKey;

          tooltips[`${transactionIndex}_${displayKey}`] = {
            content: {
              message: inferenceDetail.reason ?? '',
              icon: 'info', // TODO: icon will be changed based on source
            },
          };
        }
      );
    });
    return tooltips;
  }, [inferences]);

  return (
    <TransactionDetailSection
      transactionList={transactionList}
      fields={fields}
      isSelected={isSelected}
      tooltips={tooltips}
      warningMessage={
        warningMessage
          ? {
              enabled: isSelected && warningMessage.enabled,
              message: warningMessage.message,
            }
          : undefined
      }
    />
  );
};
